import React, { useState, useMemo } from 'react';
import { Box, Typography, Chip, TextField, MenuItem } from '@mui/material';
import { KeyboardArrowDown } from '@mui/icons-material';
import { ChatSession } from '../../types';
import { CATEGORY_OPTIONS, SORT_LABELS } from '../../constants';
import { formatTimestamp } from '../../../../common/utils';

interface MobileChatStripProps {
  sessions: ChatSession[];
  currentChatId: string;
  onSelectChat: (
    chatId: string,
    fieldType?: string,
    sessionName?: string,
    conversationType?: string
  ) => void;
  onNewChat?: () => void;
  onNewIdeation?: () => void;
  onOpenSearch?: () => void;
  category?: string;
  sortBy?: string;
  onCategoryChange?: (category: string) => void;
  onSortChange?: (sortBy: string) => void;
}

const MobileChatStrip: React.FC<MobileChatStripProps> = ({
  sessions,
  currentChatId,
  onSelectChat,
  onNewChat,
  onNewIdeation,
  onOpenSearch,
  category = 'All',
  sortBy = 'newest_first',
  onCategoryChange,
  onSortChange,
}) => {
  const [isHidden, setIsHidden] = useState(false);

  const capitalizeName = (name: string) => {
    if (!name) return name;
    return name
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const mapProcessName = useMemo(
    () => (processName: string) => {
      if (processName?.includes('Solution')) return processName;
      if (!processName) return processName;
      const exceptions = new Set(['for', 'of']);
      return processName
        .split(/[\s_]+/)
        .filter(Boolean)
        .map((word) => {
          const lowerWord = word.toLowerCase();
          return exceptions.has(lowerWord)
            ? lowerWord
            : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        })
        .join(' ');
    },
    []
  );

  const dropdownSx = {
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      borderRadius: '8px',
      fontSize: '14px',
      '& fieldset': { border: 'none !important' },
    },
    '& .MuiOutlinedInput-input': {
      padding: '8px 16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      paddingRight: '40px !important',
    },
  };

  const dropdownSelectProps = (labelText: string, value: string) => ({
    native: false,
    IconComponent: (props: any) => (
      <KeyboardArrowDown
        {...props}
        sx={{ ...props.sx, color: '#131426 !important', fontSize: 20, right: '12px !important' }}
      />
    ),
    renderValue: () => (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Typography
          sx={{ fontFamily: 'Poppins', fontSize: '13px', fontWeight: 500, color: '#737373' }}
        >
          {labelText}
        </Typography>
        <Typography
          sx={{
            fontFamily: 'Poppins',
            fontSize: '13px',
            fontWeight: 600,
            color: '#000000',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            maxWidth: '200px',
          }}
        >
          {value}
        </Typography>
      </Box>
    ),
  });

  return (
    <Box
      sx={{
        width: '100%',
        backgroundColor: '#ffffff',
        borderBottom: '1px solid #EBEBEB',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {/* Header — always visible */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          pt: 1.5,
          pb: 0.5,
        }}
      >
        <Typography
          sx={{ fontFamily: 'Poppins', fontWeight: 700, fontSize: '24px', color: '#000000' }}
        >
          Data XPLR
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {/* + New button */}
          <Box
            onClick={() => {
              onNewChat?.();
              onNewIdeation?.();
            }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              px: 1.5,
              py: 0.75,
              borderRadius: '10px',
              backgroundColor: '#E4EBFF',
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#eff6ff' },
            }}
          >
            <Typography
              sx={{ fontFamily: 'Poppins', fontSize: '14px', fontWeight: 500, color: '#000000' }}
            >
              + New
            </Typography>
          </Box>
          {/* Search icon */}
          <Box
            onClick={() => onOpenSearch?.()}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '32px',
              height: '32px',
              borderRadius: '8px',
              cursor: 'pointer',
              '&:hover': { backgroundColor: '#DDDFDE' },
            }}
          >
            <img src="/images/search-icon.png" alt="Search" width={20} height={20} />
          </Box>
        </Box>
      </Box>

      {/* Category + Sort dropdowns + session cards — only when visible */}
      {!isHidden && (
        <>
          <Box sx={{ px: 2, display: 'flex', flexDirection: 'column', gap: 0.5 }}>
            {/* Category dropdown */}
            <TextField
              select
              fullWidth
              value={category}
              onChange={(e) => onCategoryChange?.(e.target.value)}
              size="small"
              SelectProps={dropdownSelectProps('Category:', category)}
              sx={dropdownSx}
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value} sx={{ fontSize: '14px', fontFamily: 'Poppins' }}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>

            {/* Sort By dropdown */}
            <TextField
              select
              fullWidth
              value={sortBy}
              onChange={(e) => onSortChange?.(e.target.value)}
              size="small"
              SelectProps={dropdownSelectProps('Sort By:', SORT_LABELS[sortBy] || sortBy)}
              sx={dropdownSx}
            >
              <MenuItem value="newest_first" sx={{ fontSize: '14px', fontFamily: 'Poppins' }}>Newest First</MenuItem>
              <MenuItem value="oldest_first" sx={{ fontSize: '14px', fontFamily: 'Poppins' }}>Oldest First</MenuItem>
              <MenuItem value="name_asc" sx={{ fontSize: '14px', fontFamily: 'Poppins' }}>Name Ascending (A-Z)</MenuItem>
              <MenuItem value="name_desc" sx={{ fontSize: '14px', fontFamily: 'Poppins' }}>Name Descending (Z-A)</MenuItem>
              <MenuItem value="most_intakes" sx={{ fontSize: '14px', fontFamily: 'Poppins' }}>Most used to Least</MenuItem>
              <MenuItem value="least_intakes" sx={{ fontSize: '14px', fontFamily: 'Poppins' }}>Least used to Most</MenuItem>
            </TextField>
          </Box>

          {/* Horizontal scrollable session cards */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              overflowX: 'auto',
              overflowY: 'hidden',
              gap: 1.5,
              px: 2,
              pt: 1.5,
              pb: 1.5,
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
            }}
          >
            {sessions.length === 0 ? (
              <Typography sx={{ fontSize: '14px', color: '#737373', py: 2, fontFamily: 'Poppins', width: '100%', textAlign: 'center' }}>
                No knowledge objects found.
              </Typography>
            ) : (
              sessions.map((session) => (
                <Box
                  key={session.id}
                  onClick={() =>
                    onSelectChat(
                      session?.conversation_id,
                      session?.field_type || '',
                      session?.sessionName || session?.session_name || '',
                      session?.conversation_type || ''
                    )
                  }
                  sx={{
                    minWidth: '220px',
                    maxWidth: '220px',
                    flexShrink: 0,
                    border: '1px solid',
                    borderColor: currentChatId === session.conversation_id ? '#1A55F9' : '#e5e7eb',
                    borderRadius: '12px',
                    p: 2,
                    cursor: 'pointer',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.2s',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 0.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: '#313131',
                      fontSize: '15px',
                      mb: 0.5,
                      lineHeight: 1.4,
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      fontFamily: 'Poppins',
                    }}
                  >
                    {session.use_case_title || 'Untitled Process'}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#6B7280',
                      display: 'block',
                      fontSize: '11px',
                      fontWeight: 400,
                      fontFamily: 'Poppins',
                    }}
                  >
                    {session?.updated_by
                      ? `updated by ${capitalizeName(session.updated_by)} `
                      : `created by ${capitalizeName(session.created_by)} `}
                    {formatTimestamp(session.updated_at || session.created_at)}
                  </Typography>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                    <Typography
                      variant="caption"
                      sx={{
                        color: '#6B7280',
                        fontSize: '11px',
                        fontFamily: 'Poppins',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {mapProcessName(session.usedInProcess)}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: '#6B7280', fontSize: '11px', fontFamily: 'Poppins', whiteSpace: 'nowrap' }}
                    >
                      {' · '}
                      {session.ideation_intake?.length || 0} intake
                    </Typography>
                  </Box>

                  {session.conversation_type && (
                    <Chip
                      label={
                        session.conversation_type === 'solution_xplr'
                          ? 'Solution XPLR'
                          : 'Ideation XPLR'
                      }
                      size="small"
                      sx={{
                        fontFamily: 'Poppins',
                        fontSize: '10px',
                        fontWeight: 500,
                        height: '18px',
                        color: '#737373',
                        backgroundColor: 'transparent',
                        border: '1px solid #E5E7EB',
                        borderRadius: '4px',
                        mt: 1,
                        alignSelf: 'flex-start',
                      }}
                    />
                  )}
                </Box>
              ))
            )}
          </Box>
        </>
      )}

      {/* Hide / Show toggle */}
      <Box
        onClick={() => setIsHidden((prev) => !prev)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 0.5,
          py: 1,
          cursor: 'pointer',
          borderTop: '1px solid #EBEBEB',
        }}
      >
        <Typography
          sx={{ fontFamily: 'Poppins', fontSize: '14px', fontWeight: 500, color: '#131426' }}
        >
          {isHidden ? 'Show' : 'Hide'}
        </Typography>
        <Box
          component="span"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            transform: isHidden ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s ease',
            fontSize: '16px',
            color: '#131426',
            lineHeight: 1,
          }}
        >
          ∧
        </Box>
      </Box>
    </Box>
  );
};

export default MobileChatStrip;
